import React, { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useLocation, useSearchParams } from 'react-router-dom';
import {
  AppLayout,
  BreadcrumbGroup,
  ContentLayout,
  SpaceBetween,
} from '@amzn/awsui-components-react';
import { Event, Shift } from '@amzn/red-velvet-api';
import { SignupTableShiftHeader } from './signupTableShiftHeader';
import { SignupTableForShift } from './signupTableForShift';
import { useAllSignupsByShiftId } from '../../hooks/signup/useSignupsByShiftId';
import { useShiftFromUrlParams } from '../../hooks/shifts/shiftFromUrlParams';
import { getValueFromSearchParam } from '../../utils/url';
import { isShiftManageSignupBlocked } from '../../utils/signupUtils';
import { UnsupportedSignupLayout } from './coordinatorManageSignupsPageCommon';
import { publishKatalMetric } from '../katalAnalytics';
import { SIGNUP_MANAGEMENT_SINGLE_SHIFT_PAGE_VIEW } from '../../utils/constants';

interface LocationState {
  event?: Event;
  shift?: Shift;
}

interface CoordinatorManageSignupsPageForShiftProps {
  navigation: React.ReactNode;
}

export function CoordinatorManageSignupsPageForShift({
  navigation,
}: CoordinatorManageSignupsPageForShiftProps) {
  const { t } = useTranslation(['signup', 'activity', 'translation']);
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();

  const shiftId = getValueFromSearchParam(searchParams, 'shiftId', '');
  const eventId = getValueFromSearchParam(searchParams, 'eventId', '');

  // Try to get event/shift from route state first
  const locationState = location.state as LocationState | null;
  const eventFromState = locationState?.event;
  const shiftFromState = locationState?.shift;

  // Always fetch fresh data from API
  const {
    event: eventFromApi,
    shift: shiftFromApi,
    isFetched: isEventShiftCoordinatorFetched,
    isError: isEventShiftCoordinatorFetchedWithError,
  } = useShiftFromUrlParams(true);

  // Always keep data refresh: Use API data when available, fall back to route state while loading
  const event = eventFromApi || eventFromState;
  const shift = shiftFromApi || shiftFromState;
  const isEventShiftCoordinatorLoading = !isEventShiftCoordinatorFetched;

  // Fetch signups
  const {
    signups,
    isLoading: isSignupsForShiftLoading,
    isError: isSignupsForShiftError,
  } = useAllSignupsByShiftId(shiftId);

  useEffect(() => {
    publishKatalMetric(
      SIGNUP_MANAGEMENT_SINGLE_SHIFT_PAGE_VIEW,
      `eventId=${eventId}:shiftId=${shiftId}`
    );
  }, [eventId, shiftId]);

  if (isShiftManageSignupBlocked(event, shift)) {
    return <UnsupportedSignupLayout />;
  }

  return (
    <AppLayout
      footerSelector=".footer"
      navigation={navigation}
      toolsHide={true}
      breadcrumbs={
        <BreadcrumbGroup
          items={[
            { text: t('breadcrumbs.home', { ns: 'translation' }), href: '/' },
            {
              text: t('breadcrumbs.coordinatorActivity', { ns: 'activity' }),
              href: '/coordinator-activity',
            },
            { text: shift?.name || '', href: '#' },
          ]}
          onFollow={(e) => {
            e.preventDefault();
            navigate(e.detail.href);
          }}
        />
      }
      content={
        <ContentLayout>
          <SpaceBetween direction="vertical" size="l">
            <SignupTableShiftHeader
              event={event}
              shift={shift}
              isLoading={isEventShiftCoordinatorLoading}
              isError={isEventShiftCoordinatorFetchedWithError}
            />
            <SignupTableForShift
              signups={signups}
              isLoading={isSignupsForShiftLoading}
              isError={isSignupsForShiftError}
              event={event}
              shift={shift}
            />
          </SpaceBetween>
        </ContentLayout>
      }
      contentType="table"
    />
  );
}
